import type { ReactNode } from 'react';
import Overview from '../DashboardModules/Overview/Overview';
import ManagementProfileTab from '../DashboardModules/ManagementProfileTab/ManagementProfileTab';

// Context passed to every section's render() — all values already available
// in Dashboard's scope (from useLegacyBusinessDataFetch / local state).
export type MobileCompanySectionContext = {
    selectedCompany: any;
    refetchOverviewRef: any;
    appendSearchParams: (paramsToAppend: Record<string, any>) => void;
    navigateToTab: (tabId: string, additionalParams?: Record<string, string>) => void;
    triggerMain: boolean;
    mcaDocData: any;
};

export type MobileCompanySection = {
    key: string;
    label: string;
    permission?: string;
    render: (ctx: MobileCompanySectionContext) => ReactNode;
};

// The subset of a desktop `dashboardConfig.tabs` (AICA_DASHBOARD config, see
// Dashboard/index.tsx `visibleTabs`) entry mobile cares about.
type DashboardConfigTab = {
    id: string;
    label?: string;
    showInSidebar?: boolean;
};

// Renderers for sections mobile knows how to display, keyed by the same tab
// `id` desktop's dashboardConfig.tabs uses. Adding a new mobile section is
// registering a renderer here — no restructuring of Dashboard.
const MOBILE_SECTION_RENDERERS: Record<string, MobileCompanySection['render']> = {
    overview: ({ selectedCompany, refetchOverviewRef, appendSearchParams, navigateToTab, triggerMain }) => (
        <Overview
            selectedCompany={selectedCompany}
            refetchOverviewRef={refetchOverviewRef}
            appendSearchParams={appendSearchParams}
            navigateToTab={navigateToTab}
            triggerMain={triggerMain}
        />
    ),
    // The real desktop tab, not a mobile copy — its header (heading, last-updated, Source
    // badge, the filter that only appears when the config actually has filterable columns,
    // Add Director) reflows via the [data-mobile-shell] rules in its own stylesheet. One
    // header, one set of gates, nothing to drift. RECUR-27903.
    managementProfile: ({ navigateToTab, mcaDocData }) => <ManagementProfileTab navigateToTab={navigateToTab} mcaDocData={mcaDocData} />,
};

// Optional entitlement gate per section (mirrors the existing ACCESS_TIERS
// gating applied in Dashboard's `sectionPermitted`) — independent of the
// desktop tab config, since dashboardConfig.tabs carries no per-tab
// entitlement field today.
const MOBILE_SECTION_PERMISSIONS: Record<string, string | undefined> = {
    overview: undefined,
    managementProfile: undefined,
};

// Used before dashboardConfig.tabs has loaded (or if it doesn't contain a tab
// mobile knows how to render) so Overview keeps showing immediately, exactly
// as it does today, instead of blanking out mid-fetch.
const DEFAULT_TABS: DashboardConfigTab[] = [{ id: 'overview', label: 'Overview' }];

// Derives the mobile section list from the SAME dashboardConfig.tabs desktop's
// sidebar (`visibleTabs`) is built from — single source of truth for tab
// visibility/order. Only tabs mobile has a registered renderer for are
// included, so this can never surface a desktop-only tab on mobile.
export function getMobileCompanySections(desktopVisibleTabs?: DashboardConfigTab[] | null): MobileCompanySection[] {
    const tabs = desktopVisibleTabs?.length ? desktopVisibleTabs : DEFAULT_TABS;
    return tabs
        .filter((tab) => MOBILE_SECTION_RENDERERS[tab.id])
        .map((tab) => ({
            key: tab.id,
            label: tab.label ?? tab.id,
            permission: MOBILE_SECTION_PERMISSIONS[tab.id],
            render: MOBILE_SECTION_RENDERERS[tab.id],
        }));
}
